import React, { useCallback } from 'react';
import type { Props, SplitIssue } from '@atlassian/jira-software-backlog-split-issue/src/types.tsx';
import SplitIssueModal from '@atlassian/jira-software-backlog-split-issue/src/ui/split-issue-modal/async.tsx';
import { useStoreFields } from '../../../common/hooks/use-store-fields/main.tsx';
import { isObjectArrayEqual } from '../../../common/utils.tsx';
import { SplitIssueType } from '../../../model/modals/types.tsx';
import type { Card } from '../../../model/types.tsx';
import { makeServiceContext } from '../../../services/service-context/index.tsx';
import { loadAppData } from '../../../state/actions/app/index.tsx';
import { splitIssueCreate } from '../../../state/actions/cards/create/index.tsx';
import { closeModal } from '../../../state/actions/modals/index.tsx';
import { useBacklogSelector, useBacklogDispatch } from '../../../state/index.tsx';
import { getTimeTrackingOptions } from '../../../state/selectors/app/index.tsx';
import {
	getCards,
	getCardLists,
	makeGetCardListAnalyticsTypeById,
	makeGetCardListIdByCardId,
} from '../../../state/selectors/cards/index.tsx';
import {
	getEstimationFieldId,
	getEstimationType,
} from '../../../state/selectors/estimation/index.tsx';
import { issueParentsSelector } from '../../../state/selectors/issue-parent/index.tsx';
import { getOpenModalData } from '../../../state/selectors/modals/index.tsx';
import {
	makeIsCardListActiveSprint,
	isBacklogWithSprints,
} from '../../../state/selectors/sprints/index.tsx';
import { timeTrackingConfigTransformer } from '../../card-list/card/card-contents/original-estimate-field/utils.tsx';

const useSplitIssueState = (cardId: number) => {
	const isSprintsEnabled = useBacklogSelector(isBacklogWithSprints);
	const card = useBacklogSelector((state) => getCards(state)[cardId]);
	const cardListId = useBacklogSelector((state) => makeGetCardListIdByCardId(state)(cardId));
	const isCardListActiveSprint = useBacklogSelector((state) =>
		makeIsCardListActiveSprint(state)(String(cardListId)),
	);
	const serviceContext = useBacklogSelector((state) => makeServiceContext(state));

	const cardLists = useBacklogSelector(getCardLists);
	const cardListData = useBacklogSelector(
		(state) =>
			Object.values(cardLists).map(({ id, name }) => ({
				id,
				name,
				type: makeGetCardListAnalyticsTypeById(state)(id),
				isActive: makeIsCardListActiveSprint(state)(id),
			})),
		isObjectArrayEqual,
	);

	const estimationFieldId = useBacklogSelector(getEstimationFieldId);
	const estimationType = useBacklogSelector(getEstimationType);
	const timeTrackingOptions = useBacklogSelector(getTimeTrackingOptions);
	const timeTrackingConfig = timeTrackingConfigTransformer(timeTrackingOptions);

	return {
		card,
		originalIssueDestinationId: cardListId,
		cardLists: cardListData,
		estimationFieldId,
		estimationType,
		isCardListActiveSprint,
		isSprintsEnabled,
		timeTrackingConfig,
		isCompanyManaged: serviceContext.isCompanyManaged,
	};
};

export const useSplitIssueActions = () => {
	const dispatch = useBacklogDispatch();
	const issueParents = useBacklogSelector(issueParentsSelector);
	const storeFieldsActions = useStoreFields({
		issueParents,
	});

	const onClose = useCallback(() => {
		dispatch(closeModal());
	}, [dispatch]);

	const onSplitIssue = useCallback(
		(originalIssue: Card, newSplitIssues: SplitIssue[]) => {
			const newSplitIssueKeys = newSplitIssues?.map((issue) => issue?.key ?? '');
			dispatch(loadAppData({ storeFieldsActions }));
			dispatch(splitIssueCreate(originalIssue.key, newSplitIssueKeys));
			dispatch(closeModal());
		},
		[dispatch, storeFieldsActions],
	);

	return {
		onClose,
		onSplitIssue,
	};
};

type SplitIssueModalViewProps = {
	cardId: number;
};

const SplitIssueModalView = ({ cardId }: SplitIssueModalViewProps) => {
	const state = useSplitIssueState(cardId);
	const actions = useSplitIssueActions();

	const props: Props = { ...state, ...actions };

	return <SplitIssueModal {...props} />;
};

const SplitIssueModalContainer = () => {
	const modalData = useBacklogSelector(getOpenModalData);

	if (modalData?.type !== SplitIssueType) {
		return null;
	}

	const { cardId } = modalData.payload;

	return <SplitIssueModalView cardId={cardId} />;
};

export default SplitIssueModalContainer;
